import { KpiMetricType } from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionnaireEntry,
  EntryKpi
} from '../../../data/model/questionnaire'
import { getQuestionEntryPrefix } from '../../../utils/questionnaireUtils'
import { getKpiName } from './kpi.utils'
import { joinAnd } from './kpiRuleText'

type WithKpis = { kpis?: EntryKpi[] | null } | undefined

export const kpisOf = (entry: WithKpis): EntryKpi[] => entry?.kpis ?? []

export const entryHasKpi = (entry: WithKpis) => kpisOf(entry).length > 0

export const netKpisOf = (entry: WithKpis) =>
  kpisOf(entry).filter(({ metricType }) => metricType === KpiMetricType.Net)

export const rowKpisOf = (entry: WithKpis, rowId: string) =>
  kpisOf(entry).filter((kpi) => kpi.rowId === rowId)

export interface KpiImpact {
  kpi: EntryKpi
  /** True when the KPI has nothing left to count and the save removes it. */
  removed: boolean
}

/** What deleting these options (or matrix columns) does to each KPI, as the save clean-up will apply it. */
export const getOptionDeleteImpact = (
  entry: WithKpis,
  deletedOptionIds: string[]
): KpiImpact[] =>
  kpisOf(entry).flatMap((kpi) => {
    const negative = kpi.negativeOptionIds ?? []
    const isDeleted = (id: string) => deletedOptionIds.includes(id)
    if (!kpi.optionIds.some(isDeleted) && !negative.some(isDeleted)) {
      return []
    }
    const positiveLeft = kpi.optionIds.filter((id) => !isDeleted(id)).length
    const negativeLeft = negative.filter((id) => !isDeleted(id)).length
    const removed =
      kpi.metricType === KpiMetricType.Net
        ? positiveLeft === 0 || negativeLeft === 0
        : positiveLeft === 0
    return [{ kpi, removed }]
  })

const kpiNames = (kpis: EntryKpi[], subset: EntryKpi[], suffix = '') =>
  subset.map(
    (kpi) =>
      `"${getKpiName(
        kpi,
        kpis.findIndex(({ kpiId }) => kpiId === kpi.kpiId),
        kpis.length
      )}"${suffix}`
  )

const quotedNames = (kpis: EntryKpi[], subset: EntryKpi[]) =>
  joinAnd(kpiNames(kpis, subset))

const kpiNoun = (count: number) => (count > 1 ? 'KPIs' : 'KPI')

const earlierWavesSentence = (count: number) =>
  `Earlier waves keep their own ${kpiNoun(count)}.`

const getRemovedSentence = (
  names: string[],
  hasEarlierWaves: boolean,
  lead = 'This also removes'
) => {
  const removedSentence = `${lead} the ${kpiNoun(names.length)} ${joinAnd(
    names
  )} from this wave.`
  return hasEarlierWaves
    ? `${removedSentence} ${earlierWavesSentence(names.length)}`
    : removedSentence
}

/** For a confirm dialog, e.g. 'This also removes the KPI "Optimism" from this wave.', then 'Earlier waves keep their own KPI.' when there are earlier waves. */
export const describeRemovedKpis = (
  kpis: EntryKpi[],
  removed: EntryKpi[],
  hasEarlierWaves: boolean
) => getRemovedSentence(kpiNames(kpis, removed), hasEarlierWaves)

/** The summary box's Remove confirm; with earlier waves, also what marking it again does. */
export const describeKpiRemoval = (
  kpis: EntryKpi[],
  kpi: EntryKpi,
  hasEarlierWaves: boolean
) => {
  const removedSentence = getRemovedSentence(
    kpiNames(kpis, [kpi]),
    hasEarlierWaves,
    'This removes'
  )
  return hasEarlierWaves
    ? `${removedSentence} Marking it again starts a new KPI, with its trend starting from the wave you mark it in.`
    : removedSentence
}

export const LOOPED_SECTION_KPI_REASON =
  "Questions in a looped section can't be KPIs: their answers are stored once per loop."

export interface EntryKpiImpact {
  entry: DraftQuestionnaireEntry
  removed: EntryKpi[]
}

/** Every KPI on these questions, for actions that remove them whole: deleting them, or moving or looping them into a looped section. */
export const getEntriesKpiImpact = (
  entries: DraftQuestionnaireEntry[]
): EntryKpiImpact[] =>
  entries
    .filter(entryHasKpi)
    .map((entry) => ({ entry, removed: kpisOf(entry) }))

/** Names each KPI with its question, e.g. 'This also removes the KPIs "Optimism" (Q3) and "KPI" (Q5) from this wave.'; empty when nothing is removed. */
export const describeEntriesKpiImpact = (
  impacts: EntryKpiImpact[],
  hasEarlierWaves: boolean,
  lead?: string
): string => {
  const names = impacts.flatMap(({ entry, removed }) =>
    kpiNames(kpisOf(entry), removed, ` (${getQuestionEntryPrefix(entry)})`)
  )
  return names.length > 0
    ? getRemovedSentence(names, hasEarlierWaves, lead)
    : ''
}

export const describeOptionDeleteImpact = (
  kpis: EntryKpi[],
  impacts: KpiImpact[],
  item: 'option' | 'column',
  hasEarlierWaves: boolean
): string => {
  const removed = impacts.filter((impact) => impact.removed)
  const changed = impacts.filter((impact) => !impact.removed)
  const sentences: string[] = []
  if (changed.length > 0) {
    const names = quotedNames(
      kpis,
      changed.map(({ kpi }) => kpi)
    )
    sentences.push(
      `The ${kpiNoun(
        changed.length
      )} ${names} will no longer count this ${item}.`
    )
  }
  if (removed.length > 0) {
    sentences.push(
      describeRemovedKpis(
        kpis,
        removed.map(({ kpi }) => kpi),
        hasEarlierWaves
      )
    )
  } else if (changed.length > 0 && hasEarlierWaves) {
    sentences.push(earlierWavesSentence(changed.length))
  }
  return sentences.join(' ')
}
